import { useState, useEffect, useCallback, useRef } from 'react';
import { generateFollowUpPrompts } from '../services/followUpService';
import { useContacts } from '../contexts/ContactsContext';
import { getActiveWorkspaceId } from '../api/client';

const PREFS_KEY   = 'notificationPrefs';
const SNOOZED_KEY = 'followUpSnoozed';
// Prompts are workspace-scoped content, so the cache key includes the active
// workspace (same source as X-Active-Workspace) — otherwise one workspace's
// follow-up nudges would surface in another. Prefs/snooze stay user-level.
function cacheKey() {
  return `followUpCache_${getActiveWorkspaceId() || 'none'}`;
}

const FREQUENCY_MS = {
  '6h':    6  * 3600000,
  'daily': 24 * 3600000,
  'off':   Infinity,
};

function loadPrefs() {
  try {
    const stored = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
    return {
      followUpsEnabled:   stored.followUpsEnabled   ?? true,
      followUpFrequency:  stored.followUpFrequency  || '6h',
      followUpEmailDays:  stored.followUpEmailDays  || '5',
    };
  } catch {
    return { followUpsEnabled: true, followUpFrequency: '6h', followUpEmailDays: '5' };
  }
}

function loadSnoozed() {
  try { return JSON.parse(localStorage.getItem(SNOOZED_KEY) || '{}'); } catch { return {}; }
}

function saveSnoozed(s) {
  localStorage.setItem(SNOOZED_KEY, JSON.stringify(s));
}

function isCacheValid(prefs) {
  try {
    const cache = JSON.parse(localStorage.getItem(cacheKey()) || '{}');
    if (!cache.generatedAt) return false;
    const age = Date.now() - new Date(cache.generatedAt).getTime();
    const maxAge = FREQUENCY_MS[prefs.followUpFrequency] ?? FREQUENCY_MS['6h'];
    return age < maxAge;
  } catch {
    return false;
  }
}

function loadCache() {
  try {
    const cache = JSON.parse(localStorage.getItem(cacheKey()) || '{}');
    return cache.prompts || [];
  } catch {
    return [];
  }
}

function saveCache(prompts) {
  localStorage.setItem(cacheKey(), JSON.stringify({ prompts, generatedAt: new Date().toISOString() }));
}

/**
 * useFollowUp
 *
 * Generates and manages smart follow-up prompts.
 *
 * Check frequency is controlled by the user's notificationPrefs setting.
 * Prompts are cached in localStorage to avoid re-generating on every render.
 * Snoozed prompts (remind me tomorrow) are persisted across sessions.
 * Dismissed prompts are kept in component state (cleared on reload).
 */
export function useFollowUp({ tasks = [], googleEvents = [], icalEvents = [], outlookEvents = [] }) {
  const { contacts } = useContacts();
  const [allPrompts,  setAllPrompts]  = useState(loadCache);
  const [dismissed,   setDismissed]   = useState(new Set());
  const [snoozed,     setSnoozed]     = useState(loadSnoozed);

  // Keep latest data accessible to the stable generate callback
  const dataRef = useRef({ tasks, googleEvents, icalEvents, outlookEvents, contacts });
  useEffect(() => {
    dataRef.current = { tasks, googleEvents, icalEvents, outlookEvents, contacts };
  }, [tasks, googleEvents, icalEvents, outlookEvents, contacts]);

  const recalculate = useCallback(() => {
    const prefs = loadPrefs();
    if (!prefs.followUpsEnabled || prefs.followUpFrequency === 'off') {
      setAllPrompts([]);
      return;
    }
    const { tasks: t, googleEvents: g, icalEvents: ic, outlookEvents: o, contacts: c } = dataRef.current;
    const fresh = generateFollowUpPrompts(t, g, ic, o, prefs, c);
    setAllPrompts(fresh);
    saveCache(fresh);
  }, []);

  // On mount: use cache; regenerate if cache is stale
  useEffect(() => {
    const prefs = loadPrefs();
    if (!prefs.followUpsEnabled || prefs.followUpFrequency === 'off') return;
    if (!isCacheValid(prefs)) {
      recalculate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Regenerate (debounced) when source data changes
  useEffect(() => {
    const timer = setTimeout(recalculate, 1200);
    return () => clearTimeout(timer);
  }, [tasks, googleEvents, icalEvents, outlookEvents, recalculate]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  function dismiss(id) {
    setDismissed(prev => new Set([...prev, id]));
  }

  function snoozeUntilTomorrow(id) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const next = { ...snoozed, [id]: tomorrow.toISOString() };
    setSnoozed(next);
    saveSnoozed(next);
    setDismissed(prev => new Set([...prev, id]));
  }

  // Filter out dismissed and still-snoozed prompts
  const now = Date.now();
  const visiblePrompts = allPrompts.filter(p => {
    if (dismissed.has(p.id)) return false;
    const snoozeUntil = snoozed[p.id];
    if (snoozeUntil && new Date(snoozeUntil).getTime() > now) return false;
    return true;
  });

  const prefs     = loadPrefs();
  const shouldShow = prefs.followUpsEnabled
    && prefs.followUpFrequency !== 'off'
    && visiblePrompts.length > 0;

  return { prompts: visiblePrompts, dismiss, snoozeUntilTomorrow, recalculate, shouldShow };
}
